import React from 'react';
import { Button, Divider, Grid, Typography } from '@mui/material';
import { StarRatingWithCount } from '../../../../common/StarRating';
import { ITourDetail, TourVisibility } from '@lialo/core/lib/models';
import { FirstVisitorInfoList } from './FirstVisitorInfoList';
import ProfileAvatarLink from '../../../../common/ProfileAvatar/ProfileAvatarLink';
import { FollowButton } from '../../../../common/FollowButton';
import { KnownFrom } from './KnownFrom';
import TourSettingSection from '../TourSettings/TourSettingSection';
import WhatToExpectList from '../../Lists/WhatToExpectList';
import WhatYouNeedList from '../../Lists/WhatYouNeedList';
import { AverageTourRating } from '../TourRatings/AverageTourRating';
import { ReviewListComponent } from '../TourRatings/ReviewListComponent';
import { useSelector } from 'react-redux';
import { navigationSelectors } from '@lialo/core/lib/pages/common/navigation';
import { IRatingVM } from '@lialo/core/lib/pages/tourDetail/models';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { InlineTourInsight } from '../../TourInsight/InlineTourInsight';
import { useLoadReviews } from './useLoadReviews';
import { Padding } from '@lialo/pwa-ui/lib/Padding';

interface Props {
  tourCode: string;
  tour: ITourDetail;
  tourRatings: IRatingVM[];
  numberOfComments: number;
  onStarsClicked: () => void;
}

const StyledGrid = styled(Grid)({
  flexShrink: 0,
});

const StyledStarRating = styled(StarRatingWithCount)(({ theme }) => ({
  flexShrink: 0,
  margin: theme.spacing(0.5, 0, 1.5, 0),
  color: theme.palette.primary.main,
  backgroundColor: 'transparent',
  border: 'none',
  alignSelf: 'flex-start',
}));

const UserContainer = styled(Grid)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
});

const StyledAverageTourRating = styled(AverageTourRating)(({ theme }) => ({
  margin: theme.spacing(2, 1, 1),
}));

const UsernameTypography = styled(Typography)(({ theme }) => ({
  maxWidth: '100%',
  marginTop: theme.spacing(0.5),
  fontWeight: theme.typography.fontWeightMedium,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const MainSlide = ({ tourCode, tour, tourRatings, numberOfComments, onStarsClicked }: Props) => {
  const [hasMoreRatings, loadMoreRatings, isLoadingRatings] = useLoadReviews(tourCode);
  const previousLocation = useSelector(navigationSelectors.selectPreviousLocation);

  const { t } = useTranslation();

  return (
    <Padding spacingX={2} spacingY={2}>
      <Typography variant={'h6'} variantMapping={{ h6: 'h1' }}>
        {tour.title}{' '}
      </Typography>

      <StyledStarRating
        component={'button'}
        onClick={onStarsClicked}
        rating={tour.rating.averageRating}
        totalReviews={tour.rating.ratingCount}
      />

      {!previousLocation && tour.visibility === TourVisibility.PUBLIC && <FirstVisitorInfoList />}
      <StyledGrid container spacing={3}>
        <Grid item xs={9}>
          <Typography variant={'body1'}>{tour.description}</Typography>
        </Grid>
        <UserContainer item xs={3}>
          <ProfileAvatarLink hideBadge={true} size={48} user={tour.user} />
          <UsernameTypography variant={'caption'}>{tour.user.username}</UsernameTypography>
          <FollowButton authorId={tour.user.id} authorName={tour.user.username ?? ''} />
        </UserContainer>
        {tour.knownFrom.length > 0 && (
          <Grid item xs={12}>
            <KnownFrom knownFrom={tour.knownFrom} />
          </Grid>
        )}
      </StyledGrid>

      <Divider sx={{ marginTop: 1.5 }} variant={'fullWidth'} />
      <TourSettingSection
        tourCode={tourCode}
        settings={tour.settings}
        startLocation={tour.startLocation}
        endLocation={tour.endLocation}
        stops={tour.numberOfStops}
        tasks={tour.numberOfTasks}
        price={tour.price}
        translations={tour.translations}
        language={tour.language}
      />
      {tour.settings && !tour.settings.playAtHome && (
        <>
          <WhatToExpectList title={t('tour_detail.what_to_expect.title')} />
          <WhatYouNeedList title={t('tour_detail.what_you_need.title')} />
        </>
      )}
      <Divider sx={{ marginTop: 1.5 }} variant={'fullWidth'} id={'reviewSection'} />
      <Typography variant={'h6'} variantMapping={{ h6: 'h2' }} sx={{ marginTop: 2, marginBottom: 2 }}>
        {t('tour_detail.reviews.title')}
      </Typography>
      <StyledAverageTourRating
        totalRatings={tour.rating.ratingCount}
        value={tour.rating.averageRating ?? 0}
        numberOfComments={numberOfComments}
      />
      <ReviewListComponent ratings={tourRatings} />
      {hasMoreRatings && (
        <Button disabled={isLoadingRatings} onClick={loadMoreRatings}>
          {t('tour_detail.reviews.load_btn')}
        </Button>
      )}
      {tour.visibility === TourVisibility.PUBLIC && <InlineTourInsight tourCode={tourCode} />}
    </Padding>
  );
};
